import { useEffect } from 'react';
import { Loader, Text, Box } from '@mantine/core';
import { useUIStore } from '~/store/ui';

interface GlobalLoaderProps {
  isVisible: boolean;
}

export function GlobalLoader({ isVisible }: GlobalLoaderProps) {
  // Initial load için inline loading kullanıyoruz, bu sadece manuel loading için
  if (!isVisible) return null;

  return (
    <div className="global-loader-overlay fade-in">
      <div className="global-loader-content">
        <Box mb="md">
          <Loader size="xl" color="blue" type="dots" />
        </Box>
        <Text size="lg" fw={600} c="dark.6" mb="xs">
          İşlem yapılıyor...
        </Text>
        <Text size="sm" c="dimmed" ta="center" maw={300}>
          Lütfen bekleyin
        </Text>
      </div>
    </div>
  );
}

// Hook for managing page loading state
export function usePageLoader() {
  const { isPageLoading, isInitialLoad, setPageLoading, setInitialLoad } = useUIStore();

  // Development modunda daha esnek CSS yükleme kontrolü
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Function to start loading
  const startLoading = () => {
    setPageLoading(true);
  };

  // Function to stop loading
  const stopLoading = () => {
    setPageLoading(false);
    if (isInitialLoad) {
      setInitialLoad(false);
      // Inline loading'i kaldır
      const inlineLoading = document.getElementById('initial-loading');
      if (inlineLoading) {
        inlineLoading.style.opacity = '0';
        inlineLoading.style.transition = 'opacity 0.3s ease-out';
        setTimeout(() => {
          inlineLoading.remove();
          document.documentElement.style.overflow = '';
        }, 300);
      }
    }
  };

  // Function to check if resources are loaded
  const checkResourcesLoaded = () => {
    return new Promise<void>((resolve) => {
      const checkComplete = () => {
        // Check if DOM is ready
        if (document.readyState === 'complete') {
          // Check if Mantine styles are loaded
          const checkStyles = () => {
            // Çoklu kontrol yöntemi
            let stylesLoaded = false;

            // Development modunda daha esnek kontrol
            if (isDevelopment) {
              // Development'ta sadece temel kontroller yap
              const stylesheets = Array.from(document.styleSheets);
              const hasAnyStylesheet = stylesheets.length > 0;

              // Eğer herhangi bir stylesheet varsa ve DOM hazırsa, yüklendi kabul et
              if (hasAnyStylesheet && document.readyState === 'complete') {
                stylesLoaded = true;
              }
            } else {
              // Production'da tam kontrol yap
              // 1. CSS dosyalarının yüklenip yüklenmediğini kontrol et
              const stylesheets = Array.from(document.styleSheets);
              const mantineStylesLoaded = stylesheets.some(sheet => {
                try {
                  return sheet.href && (
                    sheet.href.includes('mantine') ||
                    sheet.href.includes('styles.css')
                  );
                } catch (e) {
                  return false;
                }
              });

              // 2. Test elementi ile kontrol
              const testElement = document.createElement('div');
              testElement.className = 'mantine-Button-root';
              testElement.style.visibility = 'hidden';
              testElement.style.position = 'absolute';
              testElement.style.top = '-9999px';
              document.body.appendChild(testElement);

              const computedStyle = window.getComputedStyle(testElement);
              const elementStylesLoaded = computedStyle.display !== 'inline' ||
                                        computedStyle.boxSizing === 'border-box' ||
                                        computedStyle.fontFamily.includes('Inter');

              document.body.removeChild(testElement);

              // 3. Font yüklenme kontrolü
              const fontLoaded = document.fonts ? document.fonts.check('16px Inter') : true;

              stylesLoaded = mantineStylesLoaded || elementStylesLoaded || fontLoaded;
            }

            if (stylesLoaded) {
              // Additional delay to ensure everything is rendered
              setTimeout(() => resolve(), 200);
            } else {
              // Retry after a short delay
              setTimeout(checkStyles, 150);
            }
          };

          // Wait a bit for initial rendering
          setTimeout(checkStyles, 200);
        } else {
          // Wait for DOM to be ready
          setTimeout(checkComplete, 100);
        }
      };

      checkComplete();
    });
  };

  // Auto-manage loading state
  useEffect(() => {
    if (isInitialLoad) {
      // Initial load için inline loading kullanıyoruz, bu hook sadece kontrol eder

      // Start checking resources immediately
      const startTime = Date.now();

      checkResourcesLoaded().then(() => {
        const elapsedTime = Date.now() - startTime;
        // Development'ta daha kısa loading time
        const minLoadingTime = isDevelopment ? 500 : 1500;

        // Ensure minimum loading time to prevent flashing
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          stopLoading();
        }, remainingTime);
      });
    }
  }, [isInitialLoad]);

  return {
    isLoading: isPageLoading,
    isInitialLoad,
    startLoading,
    stopLoading,
    checkResourcesLoaded
  };
}

// Default export
export default GlobalLoader;
